'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Drawer } from 'vaul';
import Icon from '@/components/shared/Icon';
import SanityImage from '@/components/shared/SanityImage';
import useToggleSmoothScroll from '@/hooks/useToggleSmoothScroll';
import { VIEWPORT_SIZES } from '@/lib/constants';
import formatVideoDuration from '@/lib/format-video-duration';

export default function Video({
  fileUrl,
  poster,
  title,
}: NonNullable<NonNullable<PageCompanyQueryResult>['hero']['video']>) {
  const t = useTranslations();
  const toggle = useToggleSmoothScroll();

  const [duration, setDuration] = useState<number | null>(null);

  useEffect(() => {
    if (!fileUrl) return;

    const video = document.createElement('video');
    video.src = fileUrl;

    video.addEventListener('loadedmetadata', () => {
      setDuration(video.duration);
    });

    return () => {
      video.removeEventListener('loadedmetadata', () => {});
    };
  }, [fileUrl]);

  if (!fileUrl) return null;

  return (
    <Drawer.Root
      autoFocus
      onOpenChange={(open) => toggle(!open)}
    >
      <Drawer.Trigger className="text-neutral-10 group mx-auto flex items-center gap-3 rounded-md bg-white p-1 pr-5 text-left ring ring-white/30 md:w-full md:max-w-[25rem] md:gap-4.5 md:rounded-lg md:p-2">
        <span className="relative isolate flex w-20 shrink-0 items-center justify-center overflow-hidden rounded-xs md:w-32 md:rounded-sm">
          <span className="group-hover:text-neutral-10 absolute z-10 flex size-5 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm transition-colors group-hover:bg-white md:size-8">
            <Icon
              className="size-2 md:size-2.5"
              name="play"
            />
          </span>

          <SanityImage
            className="aspect-video scale-105 object-cover transition-transform duration-300 group-hover:scale-100"
            image={poster}
            sizes={`(min-width: ${VIEWPORT_SIZES.md}px) 8rem, 5rem`}
          />
        </span>

        <span>
          <span className="text-2xs block max-w-44 truncate font-medium md:max-w-[14.875rem] md:text-sm">
            {title}
          </span>
          <span className="text-2xs text-neutral-20/50 block font-medium md:text-xs">
            {formatVideoDuration(duration)}
          </span>
        </span>
      </Drawer.Trigger>

      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-40 bg-black/50 backdrop-blur-xs" />
        <Drawer.Content
          className="!pointer-events-none fixed inset-0 top-auto z-50 mx-auto flex max-w-xl flex-col outline-none lg:top-0 lg:max-w-4xl lg:justify-center"
          aria-describedby={undefined}
        >
          <div className="pointer-events-auto flex max-h-[calc(100dvh-2rem)] min-h-[75vh] flex-col overflow-hidden rounded-t-lg bg-white lg:min-h-0 lg:rounded-b-lg">
            <header className="items-center justify-between gap-5 px-5 pt-4.5 pb-8 sm:px-24 md:px-16 lg:flex lg:px-8 lg:pt-7 lg:pb-5">
              <Drawer.Handle className="!w-15 lg:!hidden" />

              <Drawer.Title className="font-display text-display-2xs lg:text-display-xs mt-8 text-center font-medium uppercase lg:mt-0 lg:text-left">
                {title}
              </Drawer.Title>

              <Drawer.Close
                className="hover:text-neutral-10 hidden size-8 shrink-0 items-center justify-center rounded-md bg-black/5 transition-colors hover:bg-black/10 lg:flex"
                aria-label={t('close')}
              >
                <Icon
                  name="times"
                  className="size-3"
                />
              </Drawer.Close>
            </header>

            <div className="relative isolate flex grow flex-col overflow-hidden">
              <div className="pointer-events-none absolute inset-0 z-10 flex flex-col justify-end bg-gradient-to-t from-black/8 to-25% p-5 lg:hidden">
                <Drawer.Close className="pointer-events-auto relative mx-auto w-full max-w-2xs rounded-full bg-white p-4 font-semibold ring ring-black/8">
                  {t('close')}
                </Drawer.Close>
              </div>

              <div className="p-3.5 pt-0">
                <video
                  className="w-full rounded-md"
                  src={`${fileUrl}#t=0.1`}
                  controls
                />
              </div>
            </div>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
